import { useGetOneProduct } from "src/api/client-api/products/useGetOneProduct";
import { useGetOneProductReference } from "src/api/sure-api/productReference/useGetOneProductReference";
import { ProductMetadataContext, ProductMetadataContextInitialValues } from "./context";
import { EntityForbiddenForCampaignPage } from "src/components/PageState/EntityForbiddenForCampaignPage";
import { getRemoteErrorStatus } from "src/api/remoteCollectionState";
import { Loading } from "src/components/utils/Loading";
import { useGetSsqVersionAssignment } from "src/api/client-api/questionnaire/useGetSsqVersionAssignment.ts";
import { useGetOneSsqVersion } from "src/api/sure-api/ssq/useGetOneSsqVersion.ts";
import { ANSWER_SSQ, PRODUCT, useFoodpilotContext } from "src/context/FoodpilotContext";
import { useEffect } from "react";
import { useGetProductPosts } from "src/api/client-api/post/useGetProductPosts";
import { useGetProductPost } from "src/api/client-api/post/useGetProductPost";
import { useOlapScoresParams } from "src/context/OlapScores/useOlapScoresParams";
import { useEntityScoresOrUnavailable } from "src/context/Scores/useEntityScores";
import { hasActivityDataSpace, isCompanyOutsideProduct } from "src/services/ProductActivityDataSpace";
import { ScoreService } from "src/services/ScoreService";
import { useGetProductScores } from "src/api/client-api/olapScores/useGetProductScores";
type ProductContextProviderProps = {
    children: ReactNode;
    productId: string | undefined;
};
export const ProductMetadataContextProvider = (props: ProductContextProviderProps) => {
    const { children, productId } = props;
    const { selectedCompany, currentCampaign, canUser } = useFoodpilotContext();
    const entityScores = useEntityScoresOrUnavailable();
    const lastSelectedVariation = entityScores.isAvailable ? entityScores.lastSelectedVariation : null;

    const {
        product,
        isReady,
        error: productError,
    } = useGetOneProduct(productId, {
        companyId: selectedCompany?.id,
        campaignId: currentCampaign?.id,
    });
    const { productPosts } = useGetProductPosts(product?.id, selectedCompany?.id, currentCampaign?.id);

    const activeCompanyId = selectedCompany?.id;
    const existingPost = productPosts?.find((post) => post.organization?.id === activeCompanyId);
    const canModifySSQ = canUser(ANSWER_SSQ, PRODUCT) && !isCompanyOutsideProduct(product, selectedCompany?.id);

    const areProductCompaniesVisible = hasActivityDataSpace(product, selectedCompany?.id);

    const productTotalScoresParams = useOlapScoresParams({
        scores: {
            variations: ScoreService.getVariationByLastSelectedVariation(lastSelectedVariation),
        },
        prevScores: true,
    });

    const activePostScoresParams = useOlapScoresParams({
        scores: {
            ids: ScoreService.getScoreIdsByLastSelectedVariation(lastSelectedVariation),
            dimensions: [[["headings"]], [["ssq_versions"], ["headings"]]],
            variations: ScoreService.getVariationByLastSelectedVariation(lastSelectedVariation),
        },
    });

    const {
        data: activePost,
        isLoading: isActivePostLoading,
        isRefetching: isActivePostRefetching,
    } = useGetProductPost({
        productId: product?.id,
        companyId: selectedCompany?.id,
        campaignId: currentCampaign?.id,
        scoresQuery: activePostScoresParams.query,
        enabled: canModifySSQ || existingPost !== undefined,
    });

    const {
        data: scores,
        error: scoreQueryError,
        isError: isScoreQueryError,
        isFetching: areScoresRefetching,
        refetch: retryScoreQuery,
    } = useGetProductScores({
        productId: Number(productId),
        campaignId: currentCampaign?.id,
        companyId: selectedCompany?.id,
        scoresQuery: productTotalScoresParams.query,
        enabled: lastSelectedVariation !== null && product !== undefined && isActivePostLoading === false,
    });

    const { productReference, isReady: isProductReferenceReady } = useGetOneProductReference({
        id: product?.productReferenceId,
    });

    const { ssqVersionAssignment, isReady: isAssignmentReady } = useGetSsqVersionAssignment(
        product?.ssqId,
        currentCampaign?.id,
    );

    const ssqVersionId = ssqVersionAssignment?.ssqVersionId;

    const { ssqVersion, isReady: isSsqVersionReady } = useGetOneSsqVersion(ssqVersionId);

    useEffect(() => {
        productTotalScoresParams.state.setQuery({
            ...productTotalScoresParams.query,
            scores: {
                ...productTotalScoresParams.query.scores,
                variations: ScoreService.getVariationsByLastSelectedVariation(
                    productTotalScoresParams.query.scores?.variations,
                    lastSelectedVariation,
                ),
            },
        });

        activePostScoresParams.state.setQuery({
            ...activePostScoresParams.query,
            scores: {
                ...activePostScoresParams.query.scores,
                ids: ScoreService.getScoreIdsByLastSelectedVariation(lastSelectedVariation),
                variations: ScoreService.getVariationByLastSelectedVariation(lastSelectedVariation),
            },
        });
    }, [lastSelectedVariation?.scoreId, lastSelectedVariation?.variationId]);

    if (product === undefined && getRemoteErrorStatus(productError) === "forbidden") {
        return <EntityForbiddenForCampaignPage />;
    }

    const areAllReady = isReady && isProductReferenceReady && isAssignmentReady && isSsqVersionReady;
    if (areAllReady === false) {
        return <Loading />;
    }

    if (product === undefined || selectedCompany === null) {
        // No Product page to display possibly;
        return null;
    }

    const items: ProductMetadataContextInitialValues = {
        product,
        productReference,
        ssqVersion,
        isSsqVersionReady: isSsqVersionReady,
        scores,
        areScoresRefetching,
        scoreQueryError,
        isScoreQueryError,
        retryScoreQuery,
        activeCompanyId,
        productPosts,
        activePost,
        isActivePostRefetching,
        isActivePostLoading,
        canModifySSQ,
        areProductCompaniesVisible,
    };

    return <ProductMetadataContext.Provider value={items}>{children}</ProductMetadataContext.Provider>;
};
